import { useCallback, useState } from "react";
import { Card } from "@mui/material";

import { usePermissions } from "@/hooks/usePermissions";

import { DeleteSavedReportModal } from "../Modals/DeleteSavedReportModal";
import { SavedReportsQuery } from "../savedReports.query";
import { ReportInfo, SavedReport, SaveReportFn } from "../savedReports.types";
import { SavedReportsTable } from "../Tables/SavedReportsTable";

interface SavedReportsCardProps {
  reportInfo: ReportInfo;
  saveReport: SaveReportFn;
  /** Evidence page uses 425 today; omit elsewhere */
  minHeight?: number;
}

export const SavedReportsCard: React.FC<SavedReportsCardProps> = ({
  reportInfo,
  saveReport,
  minHeight,
}) => {
  const { hasPermission, currentUser } = usePermissions();
  const {
    data = [],
    isLoading,
    refetch,
  } = SavedReportsQuery.useGetSavedReports(reportInfo.report_id);
  const save = SavedReportsQuery.useSaveReport(saveReport);
  const remove = SavedReportsQuery.useDeleteSavedReport(reportInfo.report_id);
  const [pendingDelete, setPendingDelete] = useState<SavedReport | null>(null);
  const handleDelete = useCallback(
    (report: SavedReport) => setPendingDelete(report),
    []
  );

  const { mutate: saveMutate } = save;
  const { user_id, full_name } = currentUser;
  const report_id = reportInfo.report_id;
  const handleSave = useCallback(
    () =>
      saveMutate({
        ...reportInfo,
        created_by_id: user_id,
        created_by: full_name,
      }),
    [saveMutate, reportInfo, user_id, full_name]
  );
  const handleRefresh = useCallback(() => {
    if (report_id) refetch();
  }, [report_id, refetch]);

  return (
    <Card
      style={{
        paddingTop: "10px",
        minHeight,
        backgroundColor: "transparent",
      }}
    >
      <SavedReportsTable
        data={data}
        loading={isLoading}
        canManage={hasPermission()}
        saving={save.isPending}
        onDelete={handleDelete}
        onSave={handleSave}
        onRefresh={handleRefresh}
      />
      <DeleteSavedReportModal
        show={!!pendingDelete}
        report={pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={(report) => {
          remove.mutate(report.saved_report_id);
          setPendingDelete(null);
        }}
      />
    </Card>
  );
};
